interface PolicyOrdinal {
  title: string;
  description: string;
  note?: string;
  measurement: "ordinal";
  coding: string[];
}

interface PolicyUsd {
  title: string;
  description: string;
  note?: string;
  measurement: "usd";
}

interface PolicyFreeText {
  title: string;
  description: string;
  note?: string;
  measurement: "free_text";
}

export const POLICY_VALUE_DETAIL: {
  [key: string]: PolicyOrdinal | PolicyUsd | PolicyFreeText;
} = {
  C1: {
    title: "School closing",
    description: "Record closings of schools and universities",
    measurement: "ordinal",
    coding: [
      "no measures",
      "recommend closing",
      "require closing (only some levels or categories, eg just high school, or just public schools)",
      "require closing all levels",
    ],
  },
  C2: {
    title: "Workplace closing",
    description: "Record closings of workplaces",
    measurement: "ordinal",
    coding: [
      "no measures",
      "recommend closing (or recommend work from home)",
      "require closing (or work from home) for some sectors or categories of workers",
      "require closing (or work from home) for all-but-essential workplaces (eg grocery stores, doctors)",
    ],
  },
  C3: {
    title: "Cancel public events",
    description: "Record cancelling public events",
    measurement: "ordinal",
    coding: ["no measures", "recommend cancelling", "require cancelling"],
  },
  C4: {
    title: "Restrictions on gatherings",
    description: "Record limits on private gatherings",
    measurement: "ordinal",
    coding: [
      "no restrictions",
      "restrictions on very large gatherings (the limit is above 1000 people)",
      "restrictions on gatherings between 101-1000 people",
      "restrictions on gatherings between 11-100 people",
      "restrictions on gatherings of 10 people or less",
    ],
  },
  C5: {
    title: "Close public transport",
    description: "Record closing of public transport",
    measurement: "ordinal",
    coding: [
      "no measures",
      "recommend closing (or significantly reduce volume/route/means of transport available)",
      "require closing (or prohibit most citizens from using it)",
    ],
  },
  C6: {
    title: "Stay at home requirements",
    description:
      'Record orders to "shelter-in-place" and otherwise confine to the home',
    measurement: "ordinal",
    coding: [
      "no measures",
      "recommend not leaving house",
      `require not leaving house with exceptions for daily exercise, grocery shopping, and 'essential' trips`,
      "require not leaving house with minimal exceptions (eg allowed to leave once a week, or only one person can leave at a time, etc)",
    ],
  },
  C7: {
    title: "Restrictions on internal movement",
    description:
      "Record restrictions on internal movement between cities/regions",
    measurement: "ordinal",
    coding: [
      "no measures",
      "recommend not to travel between regions/cities",
      "internal movement restrictions in place",
    ],
  },
  C8: {
    title: "International travel controls",
    description: "Record restrictions on international travel",
    note: "This records policy for foreign travellers, not citizens",
    measurement: "ordinal",
    coding: [
      "no restrictions",
      "screening arrivals",
      "quarantine arrivals from some or all regions",
      "ban arrivals from some regions",
      "ban on all regions or total border closure",
    ],
  },
  E1: {
    title: "Income support for households",
    description:
      "Record if the government is providing direct cash payments to people who lose their jobs or cannot work.",
    note:
      "Only includes payments to firms if explicitly linked to payroll/salaries",
    measurement: "ordinal",
    coding: [
      "no income support",
      "government is replacing less than 50% of lost salary (or if a flat sum, it is less than 50% median salary)",
      "government is replacing 50% or more of lost salary (or if a flat sum, it is greater than 50% median salary)",
    ],
  },
  E2: {
    title: "Debt/contract relief for households",
    description:
      "Record if the government is freezing financial obligations for households (eg stopping loan repayments, preventing services like water from stopping, or banning evictions)",
    measurement: "ordinal",
    coding: [
      "no debt/contract relief",
      "narrow relief, specific to one kind of contract",
      "broad",
    ],
  },
  E3: {
    title: "Fiscal measures",
    description: "Announced economic stimulus spending",
    note: "Only record amount additional to previously announced spending",
    measurement: "usd",
  },
  E4: {
    title: "International support",
    description:
      "Announced offers of Covid-19 related aid spending to other countries",
    note: "Only record amount additional to previously announced spending	",
    measurement: "usd",
  },
  H1: {
    title: "Public information campaigns",
    description: "Record presence of public info campaigns",
    measurement: "ordinal",
    coding: [
      "no Covid-19 public information campaign",
      "public officials urging caution about Covid-19",
      "coordinated public information campaign (eg across traditional and social media)",
    ],
  },
  H2: {
    title: "Testing policy",
    description: "Record government policy on who has access to testing",
    note:
      "This records policies about testing for current infection (PCR tests) not testing for immunity (antibody test)",
    measurement: "ordinal",
    coding: [
      "no testing policy",
      "only those who both (a) have symptoms AND (b) meet specific criteria (eg key workers, admitted to hospital, came into contact with a known case, returned from overseas)",
      "testing of anyone showing Covid-19 symptoms",
      'open public testing (eg "drive through" testing available to asymptomatic people)',
    ],
  },
  H3: {
    title: "Contact tracing",
    description:
      "Record government policy on contact tracing after a positive diagnosis",
    note:
      "We are looking for policies that would identify all people potentially exposed to Covid-19; voluntary bluetooth apps are unlikely to achieve this",
    measurement: "ordinal",
    coding: [
      "no contact tracing",
      "limited contact tracing; not done for all cases",
      "comprehensive contact tracing; done for all identified cases",
    ],
  },
  H4: {
    title: "Emergency investment in healthcare",
    description:
      "Announced short term spending on healthcare system, eg hospitals, masks, etc",
    note: "Only record amount additional to previously announced spending",
    measurement: "usd",
  },
  H5: {
    title: "Investment in vaccines",
    description: "Announced public spending on Covid-19 vaccine development",
    note: "Only record amount additional to previously announced spending",
    measurement: "usd",
  },
  M1: {
    title: "Other",
    description: "Record policy announcements that do not fit anywhere else",
    measurement: "free_text",
  },
};
